import PageShell from "@/components/feature/PageShell";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import { siteInfo } from "@/mocks/meta";

const scopeItems = [
  {
    icon: "ri-file-text-line",
    title: "剧情介绍",
    desc: "为每一部收录作品整理结构化的剧情摘要与看点标签，方便快速判断是否符合口味。",
  },
  {
    icon: "ri-team-line",
    title: "演员信息",
    desc: "建立演员资料档案，包含个人简介与代表作，支持从演员反查其参演的影视作品。",
  },
  {
    icon: "ri-refresh-line",
    title: "更新资讯",
    desc: "追踪作品的更新进度与相关资料变更，在详情页集中呈现最新动态。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "分类与筛选",
    desc: "按电影 / 电视剧两大分类，结合类型、地区、年份多维筛选与排序，快速定位内容。",
  },
];

const usageSteps = [
  {
    step: "01",
    title: "使用导航或搜索",
    desc: "顶部导航可直接进入电影、电视剧分类；也可在搜索框输入影视名称、演员或导演关键词。",
  },
  {
    step: "02",
    title: "筛选与排序内容",
    desc: "在分类页左侧选择类型、地区、年份等多维条件，并通过热门 / 最新 / 评分切换排序方式。",
  },
  {
    step: "03",
    title: "查看详情与相关推荐",
    desc: "进入详情页阅读剧情介绍、查看演员资料与更新资讯，并通过相关推荐发现更多相似作品。",
  },
];

const faqs = [
  {
    q: "本站提供真实在线播放或下载吗？",
    a: "不提供。站内所有“在线播放”均为前端演示，仅展示封面与基本信息，不含任何真实播放接口或下载功能。",
  },
  {
    q: "收藏和分享的数据会被保存吗？",
    a: "不会。收藏与分享仅做前端操作反馈，不写入数据库，刷新页面后收藏状态不会保留。",
  },
  {
    q: "站内影视资料准确吗？",
    a: "站内内容均为演示数据，用于展示页面功能与结构，不作为真实影视资料的参考依据。",
  },
];

export default function AboutPage() {
  useDocumentMeta({
    title: "关于我们-妻子的姐姐",
    description:
      "了解妻子的姐姐影视资料平台的定位、内容范围与使用说明，查看联系方式、常见问题与版权声明。",
    keywords: "妻子的姐姐,关于我们,影视资料平台,使用说明,版权声明",
  });

  return (
    <PageShell>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
            关于「妻子的姐姐」
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {siteInfo.intro}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full space-y-12 px-4 py-12 md:px-8 md:py-16">
        <section>
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            <span className="h-5 w-1.5 rounded-full bg-accent-500" />
            网站定位与内容范围
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {scopeItems.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-700 text-background-50">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            <span className="h-5 w-1.5 rounded-full bg-accent-500" />
            使用说明
          </h2>
          <div className="mt-6 space-y-4">
            {usageSteps.map((item) => (
              <div
                key={item.step}
                className="flex items-start gap-5 rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <span className="font-heading text-2xl font-black text-accent-500 md:text-3xl">
                  {item.step}
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              <span className="h-5 w-1.5 rounded-full bg-accent-500" />
              常见问题
            </h2>
            <div className="mt-6 space-y-3">
              {faqs.map((faq) => (
                <div
                  key={faq.q}
                  className="rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <h3 className="flex items-start gap-2 font-heading text-sm font-bold text-foreground-950">
                    <i className="ri-question-line mt-0.5 text-accent-600" />
                    {faq.q}
                  </h3>
                  <p className="mt-2 pl-6 text-xs leading-relaxed text-foreground-600">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              <span className="h-5 w-1.5 rounded-full bg-accent-500" />
              联系我们
            </h2>
            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-6">
              <ul className="space-y-4 text-sm">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-primary-700">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">业务与合作邮箱</span>
                    <a
                      href={`mailto:${siteInfo.email}`}
                      className="font-medium text-foreground-900 transition-colors hover:text-primary-700"
                    >
                      {siteInfo.email}
                    </a>
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-primary-700">
                    <i className="ri-weibo-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">官方微博</span>
                    <span className="font-medium text-foreground-900">{siteInfo.weibo}</span>
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-primary-700">
                    <i className="ri-map-pin-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">站点信息</span>
                    <span className="font-medium text-foreground-900">{siteInfo.address}</span>
                  </span>
                </li>
              </ul>
              <p className="mt-5 rounded-md bg-secondary-100 p-3 text-xs leading-relaxed text-secondary-900">
                <i className="ri-information-line mr-1" />
                以上联系方式仅作展示示范，本站不收集用户提交的任何真实个人信息。
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            <span className="h-5 w-1.5 rounded-full bg-accent-500" />
            版权声明
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
            <p>
              本站为影视资料展示平台，所有影视作品名称、剧情简介、演员资料等内容均为演示数据，
              仅用于页面功能与结构的展示，不构成对任何作品的版权主张或商业用途。
            </p>
            <p>
              站内不提供真实在线播放与下载服务。如相关权利人认为内容存在异议，可通过上方邮箱与我们取得联系，我们会及时处理。
            </p>
            <p>© {new Date().getFullYear()} {siteInfo.name} · {siteInfo.icp}</p>
          </div>
        </section>
      </div>
    </PageShell>
  );
}